import { gql, useApolloClient } from "@apollo/client"
import { useCallback } from "react"
import { COACH_TOOLS_API } from "../apolloClient"
import { pgaComTld } from "../components/utils"
import { fetchAllowNextLevelPurchase } from "./acceptableProgressStandingQuery"

export const EDUCATION_PGM_PURCHASE_OFFERINGS_QUERY = gql`
  query EducationPgmPurchaseOfferings {
    me {
      educationPgmPurchaseOfferings {
        levelKey
        levelCd
        careerPath
        title
        description
        id
        productVariant {
          id
          lookupKey
          name
          priceCents
        }
      }
    }
  }
`

export const EDUCATION_PGM_LEVEL_PURCHASE_PATH = "/choose-career-path"
export const MEMBERSHIP_MOBILE_EDUCATION_PURCHASE_PATH =
  "/member/education/purchase"
export const LEVEL_2_CHOOSE_CAREER_PATH_LINK_TEXT = "Choose Career Path"

const LEGACY_EDUCATION_PURCHASE_URL_PATTERN =
  /\/(level2reg|level3reg)\.cfm\?entity_id=/i

export const isLegacyEducationPgmPurchaseUrl = url =>
  typeof url === "string" && LEGACY_EDUCATION_PURCHASE_URL_PATTERN.test(url)

export const getMembershipMobileEducationPurchaseUrl = educationFeeId => {
  if (!educationFeeId) {
    return null
  }

  return `https://${pgaComTld}${MEMBERSHIP_MOBILE_EDUCATION_PURCHASE_PATH}?educationFeeId=${encodeURIComponent(
    educationFeeId,
  )}`
}

export const redirectToMembershipMobileEducationPurchase = (
  educationFeeId,
  { allowNextLevelPurchase = false } = {},
) => {
  if (!allowNextLevelPurchase) {
    return false
  }

  const url = getMembershipMobileEducationPurchaseUrl(educationFeeId)
  if (!url) {
    return false
  }

  window.location.assign(url)
  return true
}

export const navigateToEducationPgmPurchase = async ({
  client,
  history,
  allowNextLevelPurchase = false,
}) => {
  const goToPaymentPage = () => {
    if (history?.push) {
      history.push(EDUCATION_PGM_LEVEL_PURCHASE_PATH)
    } else {
      window.location.assign(EDUCATION_PGM_LEVEL_PURCHASE_PATH)
    }
  }

  if (!allowNextLevelPurchase) {
    goToPaymentPage()
    return false
  }

  try {
    const { data } = await client.query({
      query: EDUCATION_PGM_PURCHASE_OFFERINGS_QUERY,
      context: { api: COACH_TOOLS_API },
    })
    const offerings = data?.me?.educationPgmPurchaseOfferings ?? []

    if (offerings.length === 1 && offerings[0].id) {
      return redirectToMembershipMobileEducationPurchase(offerings[0].id, {
        allowNextLevelPurchase,
      })
    }

    goToPaymentPage()
    return true
  } catch {
    goToPaymentPage()
    return false
  }
}

export const resolveEducationPgmPurchaseUrl = url => {
  if (!url) {
    return url
  }

  if (isLegacyEducationPgmPurchaseUrl(url)) {
    return EDUCATION_PGM_LEVEL_PURCHASE_PATH
  }

  return url
}

export const getLevel2PurchasePrimaryLinkText = () =>
  LEVEL_2_CHOOSE_CAREER_PATH_LINK_TEXT

export const getEducationPurchaseLinkProps = href => {
  const isInternal = typeof href === "string" && href.startsWith("/")

  return {
    href,
    ...(isInternal ? {} : { target: "_blank", rel: "noopener noreferrer" }),
  }
}

export const useResolveEducationPgmPurchaseUrl = () => {
  return useCallback(url => resolveEducationPgmPurchaseUrl(url), [])
}

export const useNavigateToEducationPgmPurchase = () => {
  const client = useApolloClient()

  return useCallback(async () => {
    const allowNextLevelPurchase = await fetchAllowNextLevelPurchase({
      client,
    })

    return navigateToEducationPgmPurchase({
      client,
      allowNextLevelPurchase,
    })
  }, [client])
}

export const useEducationPurchaseLinkProps = href => {
  const navigateToPurchase = useNavigateToEducationPgmPurchase()

  if (!href) {
    return null
  }

  if (href === EDUCATION_PGM_LEVEL_PURCHASE_PATH) {
    return {
      href,
      onClick: event => {
        event.preventDefault()
        navigateToPurchase()
      },
    }
  }

  return getEducationPurchaseLinkProps(href)
}
